import { Link } from '@tanstack/react-router';
import { Button, cn } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { SavedReportSource } from '../../model';
import {
  useOwnedSavedReportsQuery,
  useScheduledReportsQuery,
  useSharedSavedReportsQuery,
} from '../../hooks';
import { SavedReportsTable } from '../SavedReportsTable/SavedReportsTable';

interface SavedReportsPageProps {
  source: SavedReportSource;
  agencyId?: string;
}

export function SavedReportsPage({ source, agencyId }: SavedReportsPageProps) {
  const owned = useOwnedSavedReportsQuery(source === SavedReportSource.Owned, agencyId);
  const shared = useSharedSavedReportsQuery(source === SavedReportSource.Shared, agencyId);
  const scheduled = useScheduledReportsQuery(true, agencyId);
  const reports = source === SavedReportSource.Owned ? owned : shared;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <nav
        aria-label={dashboardT('nav.reporting.savedReports')}
        className="border-border bg-card page-gutter shrink-0 border-b"
      >
        <div className="flex min-w-max gap-1 overflow-x-auto">
          {savedReportTabs.map((tab) => {
            const active = tab.source === source;
            return (
              <Link
                key={tab.source}
                to={agencyId ? '/a/$agencyId/reporting/saved-reports' : tab.to}
                params={{ agencyId: agencyId ?? '' }}
                search={agencyId ? { source: tab.source } : {}}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'text-muted-foreground border-b-2 border-transparent px-3 py-3 text-sm font-medium whitespace-nowrap transition-colors',
                  active && 'shigo-supply-tab--active',
                )}
              >
                {dashboardT(tab.label)}
              </Link>
            );
          })}
        </div>
      </nav>
      <SavedReportsTable
        agencyId={agencyId}
        toolbar={
          agencyId ? (
            <Button asChild>
              <Link to="/a/$agencyId/reporting/report-explorer" params={{ agencyId }}>
                {dashboardT('nav.reporting.reportExplorer')}
              </Link>
            </Button>
          ) : undefined
        }
        source={source}
        reports={reports.data ?? []}
        schedules={scheduled.data ?? []}
        loading={reports.isLoading || scheduled.isLoading}
        error={reports.isError || scheduled.isError}
      />
    </div>
  );
}

const savedReportTabs = [
  {
    source: SavedReportSource.Owned,
    to: '/reporting/saved-reports' as const,
    label: 'savedReports.tabs.saved' as const,
  },
  {
    source: SavedReportSource.Shared,
    to: '/reporting/saved-reports/shared' as const,
    label: 'savedReports.tabs.shared' as const,
  },
];
